/**
 * ============================================================================
 * Production 模块 - 常量定义
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义状态、优先级等常量的标签映射
 * - 用于前端显示和下拉选择
 * 
 * 📖 对应后端：
 * - Model: backend/app/Modules/Production/Models/*.php
 * - 常量定义在 Model 类中
 * 
 * ⚠️ 注意事项：
 * - 常量值必须与后端 Model 中的常量值完全一致
 * - 标签文本用于前端显示，应简洁明了
 * - 所有常量使用 UPPER_SNAKE_CASE 命名
 * 
 * 🔄 更新时机：
 * - 后端新增状态或优先级时
 * - 需要修改显示文本时
 * 
 * ✅ 验证方式：
 * 让AI执行以下检查：
 * 1. 读取后端各 Model 文件
 * 2. 找到所有 const STATUS_* 和 const PRIORITY_* 定义
 * 3. 检查本文件是否包含所有常量值
 * 4. 验证常量值与后端完全一致
 * 
 * @module types/production/constants
 * @generated Manual (AI-assisted)
 * @last-updated 2025-11-26
 * ============================================================================
 */

import type {
  ProductionOrderStatus,
  ProductionOrderPriority,
  WorkOrderStatus,
  WorkOrderPriority,
  WorkOrderOperationStatus,
  WorkOrderOperationInspectionStatus,
  ScrapOrderStatus,
  ReworkOrderStatus,
  MaterialConsumptionType,
  ProductionBatchStatus,
} from './enums'

// ============================================================================
// 生产订单常量
// ============================================================================

/**
 * 生产订单状态标签映射
 * 流程：待审核 → 已审核(待接单) → 已接单 → 进行中 → 已完成
 */
export const PRODUCTION_ORDER_STATUS_LABELS: Record<ProductionOrderStatus, string> = {
  1: '待审核',
  2: '已审核',
  3: '已接单',
  4: '进行中',
  5: '已完成',
  6: '已暂停',
  7: '已取消',
}

/**
 * 生产订单优先级标签映射
 */
export const PRODUCTION_ORDER_PRIORITY_LABELS: Record<ProductionOrderPriority, string> = {
  1: '紧急',
  2: '高',
  3: '中',
  4: '低',
}

// ============================================================================
// 工单常量
// ============================================================================

/**
 * 工单状态标签映射
 */
export const WORK_ORDER_STATUS_LABELS: Record<WorkOrderStatus, string> = {
  1: '待接单',
  2: '已接单',
  3: '进行中',
  4: '已完成',
  5: '已暂停',
  6: '已取消',
}

/**
 * 工单优先级标签映射
 */
export const WORK_ORDER_PRIORITY_LABELS: Record<WorkOrderPriority, string> = {
  1: '紧急',
  2: '高',
  3: '中',
  4: '低',
}

// ============================================================================
// 工单工序常量
// ============================================================================

/**
 * 工单工序状态标签映射
 */
export const WORK_ORDER_OPERATION_STATUS_LABELS: Record<WorkOrderOperationStatus, string> = {
  1: '待开始',
  2: '进行中',
  3: '已完成',
  4: '已暂停',
  5: '已跳过',
}

/**
 * 工单工序检验状态标签映射
 */
export const WORK_ORDER_OPERATION_INSPECTION_LABELS: Record<WorkOrderOperationInspectionStatus, string> = {
  1: '待检验',
  2: '检验通过',
  3: '检验不通过',
}

// ============================================================================
// 报废单常量
// ============================================================================

/**
 * 报废单状态标签映射
 */
export const SCRAP_ORDER_STATUS_LABELS: Record<ScrapOrderStatus, string> = {
  1: '待审核',
  2: '已审核',
  3: '已拒绝',
}

// ============================================================================
// 返工单常量
// ============================================================================

/**
 * 返工单状态标签映射
 */
export const REWORK_ORDER_STATUS_LABELS: Record<ReworkOrderStatus, string> = {
  1: '待审核',
  2: '已审核',
  3: '处理中',
  4: '已完成',
  5: '已拒绝',
}

// ============================================================================
// 物料消耗常量
// ============================================================================

/**
 * 物料消耗类型标签映射
 */
export const MATERIAL_CONSUMPTION_TYPE_LABELS: Record<MaterialConsumptionType, string> = {
  WORK_ORDER: '工单消耗',
  OPERATION: '工序消耗',
}

// ============================================================================
// 生产批次常量
// ============================================================================

/**
 * 生产批次状态标签映射
 */
export const PRODUCTION_BATCH_STATUS_LABELS: Record<ProductionBatchStatus, string> = {
  IN_PROGRESS: '进行中',
  COMPLETED: '已完成',
}

// ============================================================================
// 代码生成器类型常量
// ============================================================================

/**
 * 代码生成器类型常量
 * 对应后端: CodeGeneratorService::TYPE_*
 */
export const CODE_GENERATOR_TYPES = {
  PRODUCTION_ORDER: 'production_order',
  WORK_ORDER: 'work_order',
  WORK_REPORT: 'work_report',
  MATERIAL_CONSUMPTION: 'material_consumption',
  SCRAP_ORDER: 'scrap_order',
  REWORK_ORDER: 'rework_order',
} as const

export type CodeGeneratorType = typeof CODE_GENERATOR_TYPES[keyof typeof CODE_GENERATOR_TYPES]

